import type { PhotographData } from "@qendo/profile/files";
import { type ImagePickerResult, launchImageLibraryAsync } from "expo-image-picker";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FlatList, PixelRatio } from "react-native";
import { Button } from "~/components/Button";
import { FilePickerThumbnail } from "~/components/form/gallery/FilePickerThumbnail";
import { FullPageModal } from "~/components/layout/modal/FullPageModal";
import { NoRecords } from "~/components/layout/text";
import { ErrorMessage } from "~/components/layout/text/ErrorMessage";
import { Can } from "~/components/permission/Can";
import { useErrorBag } from "~/providers/ErrorBagProvider";
import { useFeedback } from "~/providers/FeedbackProvider";
import { useGallery } from "~/providers/GalleryProvider";
import { useProfile } from "~/providers/ProfileProvider";
import { filled } from "~/util";
import { itemWidth, setWidthOnLayout } from "~/util/layout";

type MultipleFiles = string[];
type SingleFile = string;
type OptionalSingle = string | null;

export type FileValue<M extends boolean, O extends boolean = boolean> = M extends true
    ? MultipleFiles
    : O extends true
      ? OptionalSingle
      : SingleFile;

export type ImagePickerModalProps<M extends boolean, O extends boolean> = {
    items: PhotographData[];
    onRequestClose: () => void;
    readonly multiple?: M;
    readonly optional?: O;
    value?: FileValue<M, O>;
    onItemsSelected: (value: FileValue<M, O>) => void;
};

export function valueToArray(value: string | string[] | null | undefined): FileValue<true> {
    return [...new Set((value && Array.isArray(value) ? value : [value ?? ""]).filter((s) => s !== ""))];
}

export function resolveValue<M extends boolean>(
    value: string | string[] | null | undefined,
    multiple?: M,
): FileValue<M> {
    const resolved = valueToArray(value);

    return (multiple ? resolved : (resolved[0] ?? null)) as FileValue<M>;
}

export const ImagePickerModal = <M extends boolean = boolean, O extends boolean = boolean>({
    value,
    items,
    onRequestClose,
    onItemsSelected,
    optional,
    multiple,
}: ImagePickerModalProps<M, O>) => {
    const errorBag = useErrorBag<"file">();
    const [selected, setSelected] = useState<FileValue<M>>(resolveValue(value, multiple));
    const { saving, setSaving } = useFeedback();
    const { can } = useProfile();
    const { storeMedia } = useGallery();
    const [width, setWidth] = useState(300);
    const onLayout = setWidthOnLayout(setWidth);
    const columns = useMemo(() => Math.floor(width / 100), [width]);
    const item_size = useMemo(() => PixelRatio.roundToNearestPixel(itemWidth(width, columns, 0)), [width, columns]);

    // biome-ignore lint/correctness/useExhaustiveDependencies(errorBag.forget): feedback
    useEffect(() => {
        errorBag.forget("file");
    }, []);

    // biome-ignore lint/correctness/useExhaustiveDependencies(multiple): readonly
    useEffect(() => {
        setSelected(resolveValue(value, multiple));
    }, [value]);

    const can_upload = can("update", "photos");

    const isSelected = useCallback((id: string) => valueToArray(selected).includes(id), [selected]);

    const toggleSelected = (id: string) => {
        errorBag.forget("file");

        if (isSelected(id)) {
            setSelected(
                resolveValue(
                    valueToArray(selected).filter((v) => v !== id),
                    multiple,
                ),
            );
        } else {
            setSelected(resolveValue([id, ...valueToArray(selected)], multiple));
        }
    };

    const openPicker = async () => {
        errorBag.forget("file");

        if (!can_upload) {
            return;
        }

        return await launchImageLibraryAsync({
            mediaTypes: ["images"],
        }).then(async (result: ImagePickerResult) => {
            if (result.canceled) {
                return;
            }

            setSaving(true);

            await storeMedia({ asset: result.assets[0] })
                .then((item) => {
                    item && setTimeout(() => toggleSelected(item), 10);
                })
                .finally(() => setSaving(false));
        });
    };

    const uploadLocked = !can_upload || filled(saving);

    const itemsWithSelection = useMemo(
        () => items.map((i) => ({ ...i, selected: isSelected(i.id) })),
        [items, isSelected],
    );

    const saveable = !saving && (filled(optional) || filled(selected));

    const onSave = () => {
        saveable && onItemsSelected(selected);
    };

    return (
        <FullPageModal
            cancelable={!saving}
            onRequestClose={onRequestClose}
            headerRight={() => <Button disabled={!saveable} variant="quiet" size="sm" title="Save" onPress={onSave} />}
            scrollable={false}
            onBodyLayout={onLayout}
            footer={() => (
                <>
                    {errorBag.has("file") && <ErrorMessage>{errorBag.message("file")}</ErrorMessage>}
                    <Can ability="update" section="photos">
                        <Button
                            disabled={uploadLocked}
                            title="Upload from gallery"
                            icon="image"
                            variant="secondary"
                            onPress={openPicker}
                            loading={filled(saving)}
                        />
                    </Can>
                </>
            )}
        >
            {filled(items) ? (
                <FlatList
                    data={itemsWithSelection}
                    numColumns={columns}
                    key={`columns-${columns}`}
                    extraData={selected}
                    scrollEventThrottle={50}
                    renderItem={({ item }) => (
                        <FilePickerThumbnail {...item} size={item_size} onPress={() => toggleSelected(item.id)} />
                    )}
                />
            ) : (
                <NoRecords>No images available</NoRecords>
            )}
        </FullPageModal>
    );
};
